import { createEnvironmentInjector, EnvironmentInjector, Inject, inject, Injectable, InjectionToken } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ValidationMessageService } from '../../../services/validation-message.service';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { GrowlerAction, GrowlerComponent, GrowlerInput } from './growler.component';

const DEFAULT_AUTO_DISMISS_MS = 5000;
const ACTION_AUTO_DISMISS_MS = 0;

/** Growls get their own body-level layer above every dialog, outside `.cdk-overlay-container`, which kit dialogs stack over. */
@Injectable({
    providedIn: 'root',
})
export class GrowlerOverlayContainer extends OverlayContainer
{
    protected override _createContainer(): void {
        super._createContainer();
        this._containerElement.classList.replace('cdk-overlay-container', 'growler-overlay-container');
    }
}

export const GROWLER_SNACK_BAR = new InjectionToken<MatSnackBar>('GROWLER_SNACK_BAR', {
    providedIn: 'root',
    factory: () => createEnvironmentInjector(
            [{ provide: OverlayContainer, useClass: GrowlerOverlayContainer }, Overlay, MatSnackBar],
            inject(EnvironmentInjector),
    ).get(MatSnackBar),
});

export interface GrowlerOptions {
    action?: GrowlerAction;
    autoDismissAfter?: number;
}

export enum GrowlerType {
    DEFAULT = 'default',
    INFO = 'info',
    SUCCESS = 'success',
    WAIT = 'wait',
    ERROR = 'error',
    WARNING = 'warning',
}

@Injectable({
    providedIn: 'root',
})
export class GrowlerService
{
    constructor(
            @Inject(GROWLER_SNACK_BAR) private _snackBar: MatSnackBar,
            private _validationMessageService: ValidationMessageService,
    ) {
    }

    /**
     * @param title
     * @param message
     */
    public success(title: string, message: string, options?: GrowlerOptions): void {
        this.growlMessage(title, message, GrowlerType.SUCCESS, options);
    }

    /**
     * @param title
     * @param message
     */
    public error(title: string, message: string, options?: GrowlerOptions): void {
        this.growlMessage(title, message, GrowlerType.ERROR, options);
    }

    public fromErrorResponse(
            response: HttpErrorResponse,
            defaultMessage = 'An unexpected error occurred. Please try again later.',
    ): void {
        const message = this._validationMessageService.extractResponseErrorText(response, defaultMessage);
        this.oops(message);
    }

    /**
     * @param message
     */
    public oops(message: string): void {
        this.growlMessage('Oops!', message, GrowlerType.ERROR);
    }

    /**
     * @param title
     * @param message
     */
    public warning(title: string, message: string): void {
        this.growlMessage(title, message, GrowlerType.WARNING);
    }

    /**
     * Dispatch growl message to store
     * @param title
     * @param message
     * @param type
     */
    private growlMessage(title: string, message: string, type: GrowlerType, options?: GrowlerOptions): void {
        const data: GrowlerInput = {
            title,
            message,
            type,
            autoDismissAfter: options?.autoDismissAfter ?? (options?.action ? ACTION_AUTO_DISMISS_MS : DEFAULT_AUTO_DISMISS_MS),
            action: options?.action,
        };
        const snackBarOptions: MatSnackBarConfig = {
            data,
            panelClass: `growler-${type}`,
            duration: data.autoDismissAfter > 0 ? data.autoDismissAfter : undefined,
            horizontalPosition: 'left',
            verticalPosition: 'top',
        };

        this._snackBar.openFromComponent(GrowlerComponent, snackBarOptions);
    }
}
